import { useEffect, useRef, useState } from 'react';
import './Motion.css';

/* Scroll motion kit for the landing page.

   Three pieces, all CSS-driven — React never re-renders while you scroll:

   · <Reveal>     one-shot entrance (fade + rise) when the element enters view
   · <RevealText> headline that lights word by word, tied to scroll position
   · <Parallax>   element that drifts against the scroll

   Reveal is triggered by IntersectionObserver. The two scroll-linked pieces
   share useScrollProgress, which writes a single custom property (--p, 0 → 1)
   and lets CSS work out the rest. */

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

const prefersReduced = () =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Writes --p on the element as it travels from `start` to `end` of the viewport. */
const useScrollProgress = (
    ref: React.RefObject<HTMLElement>,
    start: number,
    end: number,
    settled = 0,
    disabled = false,
) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || disabled) return;

        if (prefersReduced()) { el.style.setProperty('--p', String(settled)); return; }

        let frame = 0;

        const compute = () => {
            frame = 0;
            const r = el.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const span = Math.max(1, (start - end) * vh);

            // Normal case: the element rises from `start` to `end` of the viewport.
            const byPosition = (start * vh - r.top) / span;

            // Anything near the foot of the page never reaches `end` — the
            // document runs out of scroll first — so the tail of the ramp is
            // driven by the scroll that is actually left, minus a small margin
            // so it lands just before the very last pixel.
            const doc = document.documentElement;
            const left = doc.scrollHeight - vh - window.scrollY - 48;

            el.style.setProperty('--p', String(clamp01(Math.max(byPosition, 1 - left / span))));
        };

        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(compute);
        };

        // Capture phase on the document: scroll events do not bubble, so a
        // window-only listener misses pages that scroll inside a container.
        const opts = { passive: true, capture: true } as AddEventListenerOptions;
        document.addEventListener('scroll', onScroll, opts);
        window.addEventListener('resize', onScroll);

        // Extra trigger, never a gate — covers movement without a scroll event
        // of its own (images loading above, layout shifts, anchor jumps).
        const io = typeof IntersectionObserver !== 'undefined'
            ? new IntersectionObserver(onScroll, { threshold: [0, .2, .4, .6, .8, 1] })
            : null;
        io?.observe(el);

        compute();

        return () => {
            io?.disconnect();
            if (frame) cancelAnimationFrame(frame);
            document.removeEventListener('scroll', onScroll, opts);
            window.removeEventListener('resize', onScroll);
        };
    }, [ref, start, end, settled, disabled]);
};

/** Runs `cb` once, the first time the element is seen. */
const useOnFirstSight = (ref: React.RefObject<HTMLElement>, cb: (reduced: boolean) => void) => {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        if (prefersReduced() || typeof IntersectionObserver === 'undefined') { cb(true); return; }

        const io = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            cb(false);
        }, { threshold: .3 });
        io.observe(el);

        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
};

/* ---------------------------------------------------------------- Reveal -- */

type Tag = 'div' | 'section' | 'article' | 'header' | 'p' | 'li' | 'span' | 'button';

interface RevealProps {
    children: React.ReactNode;
    as?: Tag;
    className?: string;
    /** Direction the element comes from. */
    from?: 'up' | 'left' | 'right' | 'zoom';
    /** Stagger, in ms — pass i * 80 inside a map. */
    delay?: number;
    /** Also publish scroll progress as --p, for CSS that keeps moving after
        the entrance has played (a drifting background, a filling rail). */
    track?: boolean;
    id?: string;
}

/** Fades and lifts its element into place once, the first time it is seen. */
export const Reveal = ({
    children, as: Tag = 'div', className = '', from = 'up', delay = 0, track = false, id,
}: RevealProps) => {

    const ref = useRef<HTMLElement>(null);
    const [shown, setShown] = useState(false);
    useScrollProgress(ref, track ? 1 : 0, 0, .5, !track);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        if (prefersReduced() || typeof IntersectionObserver === 'undefined') {
            setShown(true);
            return;
        }

        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                setShown(true);
                io.disconnect();          // entrance plays once, never on the way back
            },
            // Fires a little before the element is fully in, and only once it is
            // clear of the very bottom edge, so the motion is never cut off.
            { threshold: .12, rootMargin: '0px 0px -8% 0px' },
        );
        io.observe(el);

        return () => io.disconnect();
    }, []);

    return (
        <Tag
            id={id}
            ref={ref as any}
            // Part of the rendered className on purpose: callers pass a
            // className that changes (the module preview swaps a modifier on
            // every click), and React rewrites the whole attribute when it
            // does — a class added imperatively here would be wiped, leaving
            // the element stuck at opacity 0.
            className={`mo-reveal from-${from}${shown ? ' is-in' : ''} ${className}`.trim()}
            style={{ '--d': `${delay}ms` } as React.CSSProperties}
        >
            {children}
        </Tag>
    );
};

/* ------------------------------------------------------------ RevealText -- */

export interface RevealPart {
    text: string;
    accent?: boolean;   // painted with the brand gradient
}

interface RevealTextProps {
    lines: RevealPart[][];          // one inner array per visual line
    as?: 'h1' | 'h2' | 'h3' | 'p';
    className?: string;
    start?: number;                 // viewport fraction where the reveal starts
    end?: number;                   // viewport fraction where it is complete
    /** For headlines already in view on load: play the ramp once, on mount. */
    entrance?: boolean;
}

/** Headline whose words light up as it crosses the viewport. */
export const RevealText = ({
    lines, as: Tag = 'h2', className = '', start = .9, end = .48, entrance = false,
}: RevealTextProps) => {

    const ref = useRef<HTMLElement>(null);
    useScrollProgress(ref, start, end, 1);

    useEffect(() => {
        const el = ref.current;
        if (!el || !entrance || prefersReduced()) return;

        // Runs after useScrollProgress has already settled --p: wind it back to
        // 0, then hand control back so the transition carries it up again.
        el.style.setProperty('--p', '0');
        el.classList.add('mo-entrance');

        const frame = requestAnimationFrame(() => requestAnimationFrame(() => {
            document.dispatchEvent(new Event('scroll'));
        }));
        const timer = setTimeout(() => el.classList.remove('mo-entrance'), 1500);

        return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
    }, [entrance]);

    const total = lines.reduce(
        (n, line) => n + line.reduce((k, part) => k + part.text.trim().split(/\s+/).length, 0),
        0,
    );

    let index = 0;

    return (
        <Tag
            ref={ref as any}
            className={`mo-text ${className}`.trim()}
            style={{ '--n': total } as React.CSSProperties}
        >
            {lines.map((line, li) => (
                <span className="mo-line" key={li}>
                    {line.map((part, pi) =>
                        part.text.trim().split(/\s+/).map((word) => (
                            <span
                                key={`${li}-${pi}-${index}`}
                                className={`mo-word${part.accent ? ' accent' : ''}`}
                                style={{ '--i': index++ } as React.CSSProperties}
                            >
                                {word}
                            </span>
                        )),
                    )}
                </span>
            ))}
        </Tag>
    );
};

/* --------------------------------------------------------------- Counter -- */

interface CounterProps {
    /** Digits to count to. Anything non-numeric is printed as-is. */
    value: string;
    className?: string;
    duration?: number;
}

/** Counts up to its value the first time it scrolls into view. */
export const Counter = ({ value, className = '', duration = 900 }: CounterProps) => {

    const ref = useRef<HTMLElement>(null);
    const target = Number(value);
    const countable = Number.isFinite(target) && target !== 0;
    const [shown, setShown] = useState(countable ? '0' : value);

    useOnFirstSight(ref, (reduced) => {
        if (reduced || !countable) { setShown(value); return; }

        // Timestamps come from rAF, so this stays in step with the compositor.
        // The value lives in state rather than in textContent: a re-render
        // (a language switch, say) would otherwise reset the digits.
        let startedAt = 0;
        const tick = (now: number) => {
            if (!startedAt) startedAt = now;
            const t = Math.min(1, (now - startedAt) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setShown(String(Math.round(target * eased)));
            if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    });

    return <strong ref={ref as any} className={className}>{shown}</strong>;
};

/* ----------------------------------------------------------------- Track -- */

interface TrackProps {
    children?: React.ReactNode;
    as?: Tag;
    className?: string;
    start?: number;
    end?: number;
}

/** Publishes scroll progress as --p and leaves the styling to CSS. */
export const Track = ({
    children, as: Tag = 'div', className = '', start = .95, end = .4,
}: TrackProps) => {

    const ref = useRef<HTMLElement>(null);
    useScrollProgress(ref, start, end, 1);

    return <Tag ref={ref as any} className={`mo-track ${className}`.trim()}>{children}</Tag>;
};

/* -------------------------------------------------------------- Parallax -- */

interface ParallaxProps {
    children: React.ReactNode;
    as?: Tag;
    className?: string;
    /** Travel in px across the whole pass. Negative drifts the other way. */
    distance?: number;
}

/** Drifts its element against the scroll, by `distance` px top to bottom. */
export const Parallax = ({
    children, as: Tag = 'div', className = '', distance = 40,
}: ParallaxProps) => {

    const ref = useRef<HTMLElement>(null);
    // A full pass of the viewport, so --p is 0 entering and 1 leaving.
    useScrollProgress(ref, 1, 0, .5);

    return (
        <Tag
            ref={ref as any}
            className={`mo-parallax ${className}`.trim()}
            style={{ '--travel': `${distance}px` } as React.CSSProperties}
        >
            {children}
        </Tag>
    );
};

/* -------------------------------------------------------- ScrollProgress -- */

/** Hairline across the top of the page that fills as the document is read.
    Writes --sp (0 → 1); the bar itself is drawn in CSS. */
export const ScrollProgress = () => {

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let frame = 0;

        const compute = () => {
            frame = 0;
            const doc = document.documentElement;
            const max = doc.scrollHeight - window.innerHeight;
            el.style.setProperty('--sp', String(max > 0 ? clamp01(window.scrollY / max) : 0));
        };

        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(compute);
        };

        const opts = { passive: true, capture: true } as AddEventListenerOptions;
        document.addEventListener('scroll', onScroll, opts);
        window.addEventListener('resize', onScroll);
        compute();

        return () => {
            if (frame) cancelAnimationFrame(frame);
            document.removeEventListener('scroll', onScroll, opts);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    return <div ref={ref} className="mo-progress" aria-hidden="true"><span /></div>;
};

/* --------------------------------------------------------------- Scene ---- */

interface SceneProps {
    children: React.ReactNode;
    className?: string;
    /** How much scroll the scene is pinned for, in viewport heights. */
    length?: number;
}

/** A pinned scroll scene: the inner block sticks while the page scrolls past
    it, and --p runs 0 → 1 across that travel. What moves is left to CSS. */
export const Scene = ({ children, className = '', length = 1 }: SceneProps) => {

    const outer = useRef<HTMLDivElement>(null);
    const inner = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const o = outer.current, i = inner.current;
        if (!o || !i) return;

        if (prefersReduced()) { i.style.setProperty('--p', '0'); return; }

        let frame = 0;

        const compute = () => {
            frame = 0;
            const r = o.getBoundingClientRect();
            // Travel available once the sticky child has parked at the top.
            const span = Math.max(1, r.height - (window.innerHeight || 1));
            i.style.setProperty('--p', String(clamp01(-r.top / span)));
        };

        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(compute);
        };

        const opts = { passive: true, capture: true } as AddEventListenerOptions;
        document.addEventListener('scroll', onScroll, opts);
        window.addEventListener('resize', onScroll);
        compute();

        return () => {
            if (frame) cancelAnimationFrame(frame);
            document.removeEventListener('scroll', onScroll, opts);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    return (
        <div
            ref={outer}
            className={`mo-scene ${className}`.trim()}
            style={{ '--len': length } as React.CSSProperties}
        >
            <div ref={inner} className="mo-scene-pin">{children}</div>
        </div>
    );
};

/* ------------------------------------------------------------ Spotlight --- */

interface SpotlightProps {
    children: React.ReactNode;
    as?: Tag;
    className?: string;
    onClick?: () => void;
    type?: 'button';
    'aria-pressed'?: boolean;
}

/** Card that lights up under the pointer: writes --mx / --my (element-relative
    px) and --on (0/1). The glow itself is a CSS radial-gradient. */
export const Spotlight = ({
    children, as: Tag = 'div', className = '', ...rest
}: SpotlightProps) => {

    const ref = useRef<HTMLElement>(null);

    const move = (e: React.PointerEvent) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${e.clientX - r.left}px`);
        el.style.setProperty('--my', `${e.clientY - r.top}px`);
    };

    const toggle = (on: boolean) => () => ref.current?.style.setProperty('--on', on ? '1' : '0');

    return (
        <Tag
            ref={ref as any}
            className={`mo-spot ${className}`.trim()}
            onPointerMove={move}
            onPointerEnter={toggle(true)}
            onPointerLeave={toggle(false)}
            {...(rest as any)}
        >
            {children}
        </Tag>
    );
};

/* ------------------------------------------------------------- Magnetic --- */

interface MagneticProps {
    children: React.ReactNode;
    className?: string;
    /** How far the element may lean, in px. */
    strength?: number;
}

/** Leans toward the pointer while it is nearby, and springs back on leave. */
export const Magnetic = ({ children, className = '', strength = 10 }: MagneticProps) => {

    const ref = useRef<HTMLSpanElement>(null);

    const move = (e: React.PointerEvent) => {
        const el = ref.current;
        if (!el || prefersReduced()) return;
        const r = el.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        el.style.setProperty('--tx', `${clamp01(Math.abs(dx)) * Math.sign(dx) * strength}px`);
        el.style.setProperty('--ty', `${clamp01(Math.abs(dy)) * Math.sign(dy) * strength}px`);
    };

    const reset = () => {
        const el = ref.current;
        if (!el) return;
        el.style.setProperty('--tx', '0px');
        el.style.setProperty('--ty', '0px');
    };

    return (
        <span
            ref={ref}
            className={`mo-magnet ${className}`.trim()}
            onPointerMove={move}
            onPointerLeave={reset}
        >
            {children}
        </span>
    );
};
